import { Route, Routes } from '@angular/router';
import { Type } from '@angular/core';

import { ProvidersComponent } from './providers.component';
import { providerTypeGuard } from './shared/provider-type.guard';
import { SIRI_PROVIDER_TYPE } from './shared/provider-routing';
import { GTFS_PROVIDER_TYPE } from './util/gtfs-config';
import { ProviderDetailsLayoutComponent } from './shared/provider-details-layout.component';
import { SiriProviderShellComponent } from './siri/siri-provider-shell.component';
import { SiriProviderFeedsComponent } from './siri/siri-provider-feeds.component';
import { SiriProviderDetailsComponent } from './siri/siri-provider-details.component';
import { SiriFeedPageComponent } from './siri/siri-feed-page.component';
import { GtfsProviderShellComponent } from './gtfs/gtfs-provider-shell.component';
import { GtfsProviderFeedsComponent } from './gtfs/gtfs-provider-feeds.component';
import { GtfsProviderDetailsComponent } from './gtfs/gtfs-provider-details.component';
import { GtfsFeedPageComponent } from './gtfs/gtfs-feed-page.component';
import { GenericProviderShellComponent } from './generic/generic-provider-shell.component';
import { GenericProviderFeedsComponent } from './generic/generic-provider-feeds.component';
import { GenericFeedPageComponent } from './generic/generic-feed-page.component';

interface ProviderTreeComponents {
  shell: Type<unknown>;
  feeds: Type<unknown>;
  details: Type<unknown>;
  feedPage: Type<unknown>;
}

/**
 * One provider route tree: shell (Feeds | Details tabs) → feed page. Every
 * provider type with a dedicated UI gets its own tree under
 * /admin/providers/<type>/:providerId; everything else uses the generic tree
 * at /admin/providers/:providerId. `providerTypeGuard` sends a provider opened
 * in the wrong tree (old links, edited URLs) to the same page in its own tree.
 */
function providerTree(path: string, routeType: string | null, c: ProviderTreeComponents): Route {
  return {
    path,
    data: { breadcrumb: { param: 'providerId' } },
    canActivate: [providerTypeGuard(routeType)],
    children: [
      // Provider shell: Feeds | Details tabs.
      {
        path: '',
        component: c.shell,
        children: [
          { path: '', pathMatch: 'full', component: c.feeds },
          {
            path: 'details',
            component: c.details,
            title: 'pageTitle.providers',
            data: { breadcrumb: 'breadcrumb.details' },
          },
        ],
      },
      // Feed page, also the create surface (the literal 'new' segment) — a
      // full page outside the shell, like the user detail below the tenant
      // shell. A single :feedId route on purpose: a separate static 'new'
      // route would leave that route's paramMap without a 'feedId' entry at
      // all, which broke the page's create/edit detection (isCreate silently
      // read false). The page registers the 'New feed' breadcrumb label itself.
      {
        path: ':feedId',
        component: c.feedPage,
        data: { breadcrumb: { param: 'feedId' } },
      },
    ],
  };
}

/** Children of /admin/providers. Typed trees come before the generic
 *  ':providerId' tree so their literal type segment matches first. */
export const PROVIDER_ROUTES: Routes = [
  { path: '', component: ProvidersComponent },
  providerTree(`${SIRI_PROVIDER_TYPE}/:providerId`, SIRI_PROVIDER_TYPE, {
    shell: SiriProviderShellComponent,
    feeds: SiriProviderFeedsComponent,
    details: SiriProviderDetailsComponent,
    feedPage: SiriFeedPageComponent,
  }),
  providerTree(`${GTFS_PROVIDER_TYPE}/:providerId`, GTFS_PROVIDER_TYPE, {
    shell: GtfsProviderShellComponent,
    feeds: GtfsProviderFeedsComponent,
    details: GtfsProviderDetailsComponent,
    feedPage: GtfsFeedPageComponent,
  }),
  providerTree(':providerId', null, {
    shell: GenericProviderShellComponent,
    feeds: GenericProviderFeedsComponent,
    details: ProviderDetailsLayoutComponent,
    feedPage: GenericFeedPageComponent,
  }),
];
